import type * as React from 'react';
import { DashboardHeader } from '@/dashboard/dashboard-header';
import { DashboardSidebar } from '@/dashboard/dashboard-sidebar';
import { SidebarInset, SidebarProvider } from '@/ui/sidebar';
import { TooltipProvider } from '@/ui/tooltip';

interface MenuItem {
  title: string;
  url?: string;
  icon?: React.FC<React.SVGProps<SVGSVGElement>>;
  isActive?: boolean;
  items?: MenuItem[];
}

interface BreadcrumbItem {
  href?: string;
  label: string;
  isCurrentPage?: boolean;
}

interface AccountLink {
  href: string;
  label: string;
  icon?: React.ReactNode;
}

interface AppShellProps {
  logo?: React.ReactNode;
  currentServiceId?: string;
  orgId?: string;
  orgSwitcher?: React.ReactNode;
  menuItems: MenuItem[];
  user: { name?: string; email?: string; image?: string };
  breadcrumbs?: BreadcrumbItem[];
  accountLinks?: AccountLink[];
  accountLogoutLabel?: string;
  systemInfo?: React.ReactNode;
  headerTitleContent?: React.ReactNode;
  headerActions?: React.ReactNode;
  topBanner?: React.ReactNode;
  floatingPanel?: React.ReactNode;
  onLogout?: () => void;
  onNavigate?: (path: string) => void;
  children: React.ReactNode;
}

function AppShell({
  logo,
  currentServiceId,
  orgId,
  orgSwitcher,
  menuItems,
  user,
  breadcrumbs = [],
  accountLinks = [],
  accountLogoutLabel,
  systemInfo,
  headerTitleContent,
  headerActions,
  topBanner,
  floatingPanel,
  onLogout,
  onNavigate,
  children,
}: AppShellProps) {
  return (
    <TooltipProvider>
      <SidebarProvider>
        <DashboardSidebar
          logo={logo}
          currentServiceId={currentServiceId}
          orgId={orgId}
          orgSwitcher={orgSwitcher}
          menuItems={menuItems}
          user={user}
          links={accountLinks}
          logoutLabel={accountLogoutLabel}
          onLogout={onLogout}
          onNavigate={onNavigate}
        />
        <SidebarInset>
          <DashboardHeader
            breadcrumbs={breadcrumbs}
            titleContent={systemInfo ?? headerTitleContent}
            actions={headerActions}
            onNavigate={onNavigate}
          />
          {topBanner ? <div className="w-full">{topBanner}</div> : null}
          <div
            data-slot="app-shell-content"
            className="mx-auto flex w-full max-w-screen-2xl flex-1 flex-col px-4 py-5 md:px-6 md:py-6 xl:px-8"
          >
            {children}
          </div>
        </SidebarInset>
        {floatingPanel}
      </SidebarProvider>
    </TooltipProvider>
  );
}

export type { AppShellProps, MenuItem };
export { AppShell };
